// 结果下载页 · 新主页设计语言（ink/paper/signal/acid）· 每卡片渲染真实 PPT 页面预览
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { downloadFile } from "../lib/download";
import SlideCanvas from "../components/SlideCanvas";
import { BrandLogo } from "../components/BrandLogo";
import "./Deck.css";

type DeckInfo = Awaited<ReturnType<typeof api.getDeck>>;

export default function Deck() {
  const { deckId } = useParams();
  const [data, setData] = useState<DeckInfo | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 048 F3：配图降级告知——读 artwork 步骤产物，失败张数 >0 时横幅 + 重试入口
  const [artFailed, setArtFailed] = useState(0);
  // 049 F10：失败原因按分类如实展示
  const [artReason, setArtReason] = useState("图像服务暂时不可用");
  const [retrying, setRetrying] = useState(false);
  const [retryMsg, setRetryMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!deckId) return;
    api.getDeck(deckId).then((d) => {
      setData(d);
      const jobId = d.deck.job_id;
      if (jobId) {
        api.getJob(jobId).then((j) => {
          const art = (j.steps as { step: string; output?: { stats?: { failed?: number; perImage?: { ok: boolean; error?: string }[] } } }[])
            .filter((s) => s.step === "artwork")
            .slice(-1)[0];
          setArtFailed(art?.output?.stats?.failed ?? 0);
          const lastErr = art?.output?.stats?.perImage?.filter((p) => !p.ok && p.error).slice(-1)[0]?.error;
          const m = lastErr?.match(/^\[(\w+)\]/);
          const map: Record<string, string> = {
            http_5xx: "图像服务网关繁忙（HTTP 5xx，供应商侧未创建任务）",
            http_429: "图像服务限流（HTTP 429）",
            http_4xx: "图像请求参数/鉴权异常（HTTP 4xx）",
            network: "图像请求未发出（网络/TLS 中断）",
            timeout: "图像生成超时",
            no_url: "图像服务响应异常（未返回图片地址）",
          };
          if (m && map[m[1]]) setArtReason(map[m[1]]);
        }).catch(() => undefined);
      }
    }).catch((e) => setErr((e as Error).message));
  }, [deckId]);

  async function download() {
    if (!deckId) return;
    setBusy(true);
    try {
      const { url, filename } = await api.exportDeck(deckId);
      await downloadFile(url, filename); // blob 下载 + 干净中文名（绕开畸形响应头）
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="aim-deckpage">
      <header className="aim-deckpage-header">
        <Link className="aim-deckpage-wordmark" to="/projects" aria-label="策略咖 · 方案结果">
          <BrandLogo />
          <span>· 方案结果</span>
        </Link>
        <nav>
          <Link to="/projects">← 我的方案舱</Link>
          <Link to="/new">＋ 新建方案</Link>
        </nav>
      </header>

      <main className="aim-deckpage-main">
        {err && <div className="aim-deckpage-error" role="alert">{err}</div>}
        {!data && !err && <div className="aim-deckpage-loading"><p>正在读取方案…</p></div>}

        {data && (
          <>
            <p className="aim-deckpage-eyebrow">DECK / RESULT · {data.deck.style === "legacy" ? "经典模板" : data.deck.theme_id ?? "swiss-ikb"}</p>
            <section className="aim-deckpage-head">
              <div>
                <h1>{data.deck.title}</h1>
                <p className="meta">共 {data.slides.length} 页 · {data.deck.style === "legacy" ? "经典模板" : data.deck.theme_id ?? "swiss-ikb"}</p>
              </div>
              <div className="aim-deckpage-actions">
                {data.deck.job_id && (
                  <Link className="aim-deck-btn aim-deck-btn-outline" to={`/job/${data.deck.job_id}`} title="回看这次生成的完整过程与各工具产物">
                    生成过程 <span aria-hidden="true">↗</span>
                  </Link>
                )}
                <Link className="aim-deck-btn aim-deck-btn-dark" to={`/deck/${deckId}/edit`}>在线编辑 <span aria-hidden="true">↗</span></Link>
                <button className="aim-deck-btn aim-deck-btn-outline" onClick={download} disabled={busy || !data.downloadUrl}>
                  {busy ? "生成链接…" : "下载 PPTX"}
                </button>
              </div>
            </section>

            {artFailed > 0 && data.deck.job_id && (
              <div className="aim-deck-notice is-warn">
                ⚠ 本次有 <b>{artFailed}</b> 张 AI 配图生成失败（{artReason}），对应图文页目前为占位框。
                {retryMsg ? <span> {retryMsg}</span> : (
                  <button
                    type="button" className="aim-deck-btn aim-deck-btn-outline" style={{ marginLeft: 8 }}
                    disabled={retrying}
                    onClick={async () => {
                      setRetrying(true);
                      try {
                        await api.retryArtwork(data.deck.job_id!);
                        setRetryMsg("✅ 已启动配图补出，任务完成后刷新本页即可看到新图。");
                      } catch (e) {
                        setRetryMsg(`重试失败：${(e as Error).message}`);
                      }
                      setRetrying(false);
                    }}
                  >{retrying ? "启动中…" : "重新生成配图"}</button>
                )}
              </div>
            )}

            {data.deck.style === "magazine" && (
              <div className="aim-deck-notice">
                杂志风标题使用衬线字体「思源宋体」。若打开 pptx 后标题字体不像样张，
                请安装免费字体包（约 10MB）：搜索「思源宋体」或访问
                <a href="https://github.com/adobe-fonts/source-han-serif/releases" target="_blank" rel="noreferrer">思源宋体下载页</a>
                ，安装后重新打开即可。
              </div>
            )}

            <div className="aim-deck-section-title">
              <p className="aim-deckpage-eyebrow" style={{ margin: 0 }}>PAGES / 真实页面预览</p>
              <span>{String(data.slides.length).padStart(2, "0")} PAGES</span>
            </div>

            <div className="aim-deck-grid">
              {data.slides.map((s, i) => (
                <article className="aim-deck-card" key={s.id}>
                  <div className="aim-deck-card-preview">
                    <div><SlideCanvas slide={s.content} pageIdx={s.idx + 1} style={data.deck.style} themeId={data.deck.theme_id} totalPages={data.slides.length} /></div>
                    <b>{String(i + 1).padStart(2, "0")}</b>
                  </div>
                  <div className="aim-deck-card-body">
                    <p className="layout">{s.layout}</p>
                    <h3>{s.content.title ?? "（无标题）"}</h3>
                    {s.content.subtitle && <p>{s.content.subtitle}</p>}
                  </div>
                </article>
              ))}
            </div>

            <p className="aim-deckpage-foot">下载链接 15 分钟内有效；点「在线编辑」可在网页内改文字、换配图、调页序后再导出。</p>
          </>
        )}
      </main>
    </div>
  );
}
